import React from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogProps,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { DesignMode, getDesignTokens } from './designTokens';
import { SiteButton } from './SiteButton';
import { StatusPill } from './StatusPill';

export type CrystalModalProps = Omit<DialogProps, 'children'> & {
  mode?: DesignMode;
  eyebrow?: string;
  title: string;
  description?: string;
  children?: React.ReactNode;
  primaryAction?: string;
  secondaryAction?: string;
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnEscapeKey?: boolean;
  onClose: () => void;
};

export const CrystalModal = ({
  mode = 'light',
  eyebrow = 'Modal',
  title,
  description,
  children,
  primaryAction = 'Confirm',
  secondaryAction = 'Cancel',
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscapeKey = true,
  onClose,
  ...props
}: CrystalModalProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const tokens = getDesignTokens(mode);

  return (
    <Dialog
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={!closeOnEscapeKey}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick' && !closeOnBackdropClick) return;
        if (reason === 'escapeKeyDown' && !closeOnEscapeKey) return;
        onClose();
      }}
      PaperProps={{
        sx: {
          m: { xs: 0, sm: 2 },
          width: { xs: '100%', sm: 'min(760px, calc(100vw - 48px))' },
          minHeight: { xs: '100%', sm: 420 },
          borderRadius: { xs: 0, sm: 3.5 },
          overflow: 'hidden',
          border: `1px solid ${
            mode === 'light' ? 'rgba(15,23,42,0.12)' : 'rgba(255,255,255,0.16)'
          }`,
          background:
            mode === 'light'
              ? 'linear-gradient(135deg, rgba(255,255,255,0.82), rgba(255,255,255,0.48))'
              : 'linear-gradient(135deg, rgba(18,28,42,0.9), rgba(10,17,28,0.82))',
          boxShadow:
            mode === 'light'
              ? 'inset 0 1.5px 0 rgba(255,255,255,0.96), inset 0 -1px 0 rgba(15,23,42,0.08), 0 28px 80px rgba(15,23,42,0.22), 0 0 0 1px rgba(255,255,255,0.7)'
              : 'inset 0 1.5px 0 rgba(255,255,255,0.16), inset 0 -1px 0 rgba(0,0,0,0.36), 0 28px 80px rgba(0,0,0,0.5)',
          backdropFilter: 'blur(28px) saturate(175%) brightness(1.04)',
          WebkitBackdropFilter: 'blur(28px) saturate(175%) brightness(1.04)',
          color: tokens.colors.text
        }
      }}
      BackdropProps={{
        sx: {
          background:
            mode === 'light' ? 'rgba(15,23,42,0.28)' : 'rgba(0,0,0,0.64)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)'
        }
      }}
      {...props}
    >
      <DialogContent
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: '100%', sm: 420 },
          p: { xs: 2.5, sm: 4 },
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            zIndex: -1,
            background:
              mode === 'light'
                ? 'radial-gradient(circle at 88% 18%, rgba(65,200,196,0.18), transparent 28%), linear-gradient(180deg, rgba(255,255,255,0.54), rgba(255,255,255,0) 58%)'
                : 'radial-gradient(circle at 88% 18%, rgba(65,200,196,0.18), transparent 28%), linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0) 58%)'
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2
          }}
        >
          <StatusPill mode={mode} tone="info">
            {eyebrow}
          </StatusPill>
          {showCloseButton && (
            <IconButton
              aria-label="Close modal"
              onClick={onClose}
              sx={{
                width: 40,
                height: 40,
                color: tokens.colors.text,
                border: `1px solid ${tokens.colors.border}`,
                background:
                  mode === 'light'
                    ? 'rgba(255,255,255,0.48)'
                    : 'rgba(255,255,255,0.06)',
                backdropFilter: 'blur(14px)',
                '&:hover': {
                  background:
                    mode === 'light'
                      ? 'rgba(255,255,255,0.68)'
                      : 'rgba(255,255,255,0.1)'
                }
              }}
            >
              <CloseRoundedIcon />
            </IconButton>
          )}
        </Box>

        <Box sx={{ mt: { xs: 4, sm: 5 }, maxWidth: 620 }}>
          <Typography
            component="h2"
            sx={{
              color: tokens.colors.text,
              fontSize: { xs: 34, sm: 44 },
              fontWeight: 900,
              lineHeight: 1.05
            }}
          >
            {title}
          </Typography>
          {description && (
            <Typography
              sx={{
                mt: 2,
                color: tokens.colors.textMuted,
                fontSize: { xs: 15, sm: 17 },
                lineHeight: 1.7
              }}
            >
              {description}
            </Typography>
          )}
        </Box>

        {children && <Box sx={{ mt: 3 }}>{children}</Box>}

        <Box
          sx={{
            mt: 'auto',
            pt: { xs: 4, sm: 5 },
            display: 'flex',
            justifyContent: 'flex-end',
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            gap: 1.2
          }}
        >
          <SiteButton mode={mode} tone="quiet" onClick={onClose}>
            {secondaryAction}
          </SiteButton>
          <SiteButton mode={mode} tone="success">
            {primaryAction}
          </SiteButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
